import React, { useEffect, useMemo, useState, useCallback } from "react";
import {
  Box,
  Tabs,
  Tab,
  TextField,
  Stack,
  Button,
  Typography,
  Paper,
  Switch,
  Chip,
  Tooltip,
  IconButton,
} from "@mui/material";
import { DataGrid, GridToolbarQuickFilter } from "@mui/x-data-grid";
import AddBoxIcon from "@mui/icons-material/AddBox";
import RefreshIcon from "@mui/icons-material/Refresh";
import PublicIcon from "@mui/icons-material/Public";
import LockIcon from "@mui/icons-material/Lock";
import LockOpenIcon from "@mui/icons-material/LockOpen";
import { Redirect } from "react-router-dom";
import { toast } from "react-toastify";
import LocalstorageHelper from "../../LocalStorageHelper/LocalstorageHelper";
import AdminSidebar from "../../Components/AdminPanel/AdminSidebar";

toast.configure();

const endpoint = {
  all: "/admin/ipconfig/allips",
  insert: "/admin/ipconfig/insert",
  update: "/admin/ipconfig/update",
  myip: "/admin/ipconfig/myapi",
};

const isValidIp = (ip) => {
  if (!ip) return false;
  const ipv4 = /^(25[0-5]|2[0-4]\d|1?\d?\d)(\.(25[0-5]|2[0-4]\d|1?\d?\d)){3}$/;
  const ipv6 =
    /^(([0-9a-fA-F]{1,4}:){1,7}[0-9a-fA-F]{1,4}|::|([0-9a-fA-F]{1,4}:){1,6}:|:([0-9a-fA-F]{1,4}:){1,6})$/;
  return ipv4.test(ip) || ipv6.test(ip);
};

const ribbonSx = {
  px: 2,
  py: 1.5,
  borderRadius: 3,
  background:
    "linear-gradient(90deg, rgba(59,130,246,0.18), rgba(236,72,153,0.18))",
  border: "1px dashed rgba(99,102,241,0.35)",
};

const cardSx = {
  p: 2,
  borderRadius: 3,
  boxShadow: "0 10px 30px rgba(0,0,0,0.12)",
  background:
    "linear-gradient(135deg, rgba(103,178,111,0.15), rgba(63,81,181,0.15))",
  border: "1px solid rgba(99,102,241,0.25)",
};

const toolbarWrapSx = {
  display: "flex",
  gap: 1,
  alignItems: "center",
  flexWrap: "wrap",
};

function QuickToolbar({ onRefresh, disabled }) {
  return (
    <Box sx={toolbarWrapSx}>
      <GridToolbarQuickFilter placeholder="Search IPs…" />
      <Tooltip title="Refresh">
        <span>
          <IconButton onClick={onRefresh} disabled={disabled}>
            <RefreshIcon />
          </IconButton>
        </span>
      </Tooltip>
    </Box>
  );
}

const IpConfig = () => {
  const userToken = LocalstorageHelper.GetAdminToken();

  const [tab, setTab] = useState(0);
  const [ipInput, setIpInput] = useState("");

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selection, setSelection] = useState([]);

  // triggers for effects
  const [refreshTick, setRefreshTick] = useState(0);         // list refresh trigger
  const [insertReq, setInsertReq] = useState(null);          // { ipaddress }
  const [toggleReq, setToggleReq] = useState(null);          // { ipaddress, newStatus, prevStatus }
  const [myIpTick, setMyIpTick] = useState(0);               // show-my-ip trigger
  const [insertLoading, setInsertLoading] = useState(false); // UI state for insert btn

  const authHeaders = useMemo(
    () => ({
      "Content-Type": "application/json",
      token: userToken,
    }),
    [userToken]
  );

  // ============= EFFECT: fetch list (on mount + when refreshTick changes) ============
  useEffect(() => {
    let isCancelled = false;
    const run = async () => {
      try {
        setLoading(true);
        const res = await fetch(endpoint.all, {
          method: "GET",
          headers: { token: userToken },
        });
        const data = await res.json();
        if (isCancelled) return;
        const withIds = (data || []).map((r) => ({ id: r.ipaddress, ...r }));
        setRows(withIds);
      } catch (err) {
        if (!isCancelled) {
          console.log(err.message);
          toast.error("Failed to load IPs");
        }
      } finally {
        if (!isCancelled) setLoading(false);
      }
    };
    if (userToken) run();
    return () => {
      isCancelled = true;
    };
  }, [userToken, refreshTick]);

  // ============= EFFECT: insert new IP ==============================================
  useEffect(() => {
    let isCancelled = false;
    const run = async () => {
      if (!insertReq) return;
      setInsertLoading(true);
      try {
        const res = await fetch(endpoint.insert, {
          method: "POST",
          headers: { ...authHeaders },
          body: JSON.stringify({ ipaddress: insertReq.ipaddress }),
        });
        const out = await res.json();
        if (res.ok) {
          toast.success(out?.message || "IP Registered Successfully!!");
          setIpInput("");
          setRefreshTick((t) => t + 1);
        } else {
          toast.error(out || "Error Registering IP");
        }
      } catch (err) {
        console.log(err.message);
        toast.error("Insert failed");
      } finally {
        if (!isCancelled) {
          setInsertLoading(false);
          setInsertReq(null);
        }
      }
    };
    run();
    return () => {
      isCancelled = true;
    };
  }, [insertReq, authHeaders]);

  // ============= EFFECT: toggle activeflag ==========================================
  useEffect(() => {
    let isCancelled = false;
    const run = async () => {
      if (!toggleReq) return;
      const { ipaddress, newStatus, prevStatus } = toggleReq;
      try {
        const res = await fetch(endpoint.update, {
          method: "PUT",
          headers: { ...authHeaders },
          body: JSON.stringify({ ipaddress, activeflag: newStatus }),
        });
        const out = await res.json();
        if (res.ok) {
          toast.success(out?.message || "IP Status Updated!");
        } else {
          // revert on failure
          setRows((prev) =>
            prev.map((r) =>
              r.ipaddress === ipaddress ? { ...r, activeflag: prevStatus } : r
            )
          );
          toast.error(out || "Error Updating IP");
        }
      } catch (err) {
        console.log(err.message);
        // revert on error
        setRows((prev) =>
          prev.map((r) =>
            r.ipaddress === ipaddress ? { ...r, activeflag: prevStatus } : r
          )
        );
        toast.error("Failed to update status");
      } finally {
        if (!isCancelled) setToggleReq(null);
      }
    };
    run();
    return () => {
      isCancelled = true;
    };
  }, [toggleReq, authHeaders]);

  // ============= EFFECT: show my IP (fetch from server) =============================
  useEffect(() => {
    let isCancelled = false;
    const run = async () => {
      if (myIpTick === 0) return;
      try {
        const res = await fetch(endpoint.myip, {
          method: "GET",
          headers: { token: userToken },
        });
        const data = await res.json();
        if (res.ok) {
          if (!isCancelled) {
            setIpInput(data.cleanip);
            toast.info(`Your IP: ${data.cleanip}`);
          }
        } else {
          toast.error(data.error || "Failed to fetch your IP");
        }
      } catch (err) {
        console.error(err.message);
        toast.error("Internal Error Fetching IP");
      }
    };
    run();
    return () => {
      isCancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [myIpTick, userToken]);

  // -------------------- handlers (no fetches; only set triggers) --------------------
  const requestRefresh = useCallback(
    (e) => {
      if (e?.preventDefault) e.preventDefault();
      setRefreshTick((t) => t + 1);
    },
    []
  );

  const requestInsert = useCallback(
    (e) => {
      if (e?.preventDefault) e.preventDefault();
      if (!ipInput) {
        toast.warning("Enter an IP address");
        return;
      }
      if (!isValidIp(ipInput)) {
        toast.warning("Please enter a valid IPv4 or IPv6 address");
        return;
      }
      setInsertReq({ ipaddress: ipInput });
    },
    [ipInput]
  );

  const requestToggle = useCallback((ipaddress, newStatus) => {
    // optimistic UI first
    setRows((prev) =>
      prev.map((r) =>
        r.ipaddress === ipaddress ? { ...r, activeflag: newStatus } : r
      )
    );
    // queue effect with previous status to revert if needed
    const prev = rows.find((r) => r.ipaddress === ipaddress)?.activeflag ?? !newStatus;
    setToggleReq({ ipaddress, newStatus, prevStatus: prev });
  }, [rows]);

  const requestMyIp = useCallback((e) => {
    if (e?.preventDefault) e.preventDefault();
    setMyIpTick((t) => t + 1);
  }, []);

  // -------------------- grid columns --------------------
  const columns = [
    {
      field: "ipaddress",
      headerName: "IP Address",
      flex: 1,
      minWidth: 200,
      renderCell: (params) => (
        <Stack direction="row" alignItems="center" spacing={1}>
          {params.row.activeflag ? (
            <LockOpenIcon fontSize="small" />
          ) : (
            <LockIcon fontSize="small" />
          )}
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            {params.value}
          </Typography>
        </Stack>
      ),
    },
    {
      field: "activeflag",
      headerName: "Active",
      width: 120,
      align: "center",
      headerAlign: "center",
      renderCell: (params) => (
        <Switch
          checked={Boolean(params.value)}
          onChange={(e) => requestToggle(params.row.ipaddress, e.target.checked)}
        />
      ),
    },
    {
      field: "status",
      headerName: "Status",
      width: 140,
      valueGetter: (p) => (p.row.activeflag ? "Active" : "Inactive"),
      renderCell: (p) => (
        <Chip
          label={p.value}
          color={p.row.activeflag ? "success" : "default"}
          variant={p.row.activeflag ? "filled" : "outlined"}
          size="small"
        />
      ),
    },
  ];

  const CustomToolbar = () => (
    <QuickToolbar onRefresh={requestRefresh} disabled={loading} />
  );

  if (!userToken) return <Redirect exact to="/" />;

  return (
    <>
      <AdminSidebar />
      <Box sx={{ p: { xs: 1.5, md: 3 } }}>
        <Box sx={ribbonSx}>
          <Typography variant="h5" sx={{ fontWeight: 800 }}>
            IP Configuration
          </Typography>
          <Typography variant="body2" sx={{ opacity: 0.8 }}>
            Register new IPs and toggle access quickly. Optimized, responsive and colorful UI.
          </Typography>
        </Box>

        <Paper sx={{ mt: 2, borderRadius: 3, overflow: "hidden" }}>
          <Tabs
            value={tab}
            onChange={(_, v) => setTab(v)}
            variant="scrollable"
            scrollButtons="auto"
            sx={{
              background:
                "linear-gradient(90deg, rgba(59,130,246,0.18), rgba(236,72,153,0.18))",
            }}
          >
            <Tab label="Register IP" />
            <Tab label="Manage IPs" />
          </Tabs>

          {/* Register IP */}
          {tab === 0 && (
            <Box sx={cardSx}>
              <Stack
                direction={{ xs: "column", sm: "row" }}
                spacing={2}
                alignItems="center"
                justifyContent="center"
              >
                <TextField
                  label="IP Address"
                  placeholder="e.g. 202.8.116.193"
                  size="small"
                  sx={{ flex: 1, minWidth: 250 }}
                  value={ipInput}
                  onChange={(e) => setIpInput(e.target.value.trim())}
                />

                <Stack direction="row" spacing={1}>
                  <Button
                    variant="contained"
                    startIcon={<AddBoxIcon />}
                    onClick={requestInsert}
                    disabled={insertLoading}
                    sx={{
                      borderRadius: 2,
                      textTransform: "none",
                      fontWeight: 700,
                    }}
                  >
                    {insertLoading ? "Registering..." : "Add To Whitelist"}
                  </Button>

                  <Button
                    variant="outlined"
                    color="info"
                    startIcon={<PublicIcon />}
                    onClick={requestMyIp}
                    sx={{
                      borderRadius: 2,
                      textTransform: "none",
                      fontWeight: 700,
                    }}
                  >
                    Show My IP
                  </Button>
                </Stack>
              </Stack>
            </Box>
          )}

          {/* Manage IPs */}
          {tab === 1 && (
            <Box sx={{ p: 2 }}>
              <div style={{ height: 520, width: "100%" }}>
                <DataGrid
                  rows={rows}
                  columns={columns}
                  loading={loading}
                  disableRowSelectionOnClick
                  onRowSelectionModelChange={(m) => setSelection(m)}
                  rowSelectionModel={selection}
                  slots={{ toolbar: CustomToolbar }}
                  sx={{
                    border: "1px solid rgba(99,102,241,0.25)",
                    borderRadius: 2,
                    "& .MuiDataGrid-columnHeaders": {
                      background:
                        "linear-gradient(180deg, rgba(99,102,241,0.18), rgba(236,72,153,0.18))",
                      fontWeight: 700,
                    },
                    "& .MuiDataGrid-cell": { fontSize: 14 },
                  }}
                />
              </div>

              <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                mt={1.5}
              >
                <Typography variant="caption" sx={{ opacity: 0.8 }}>
                  Total: {rows.length}
                </Typography>
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<RefreshIcon />}
                  onClick={requestRefresh}
                >
                  Refresh
                </Button>
              </Stack>
            </Box>
          )}
        </Paper>
      </Box>
    </>
  );
};

export default IpConfig;
